import * as React from 'react';
import {
  Check,
  ChevronDown,
  Clock,
  Pause,
  Pencil,
  Play,
  Plus,
  Power,
  Trash2,
  X,
} from 'lucide-react';
import { descreverCron, proximaExecucaoCron, validarCron } from '@wms/shared';
import {
  useCatalogoRotinas,
  useAtivarTarefa,
  useExcluirTarefa,
  useExecucoesTarefa,
  useExecutarTarefa,
  useSalvarTarefa,
  useTarefas,
  type DadosTarefa,
  type RotinaCatalogo,
  type TarefaAdmin,
} from '@/hooks/admin';
import { useContexto } from '@/hooks/useContexto';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import {
  CabecalhoCartao,
  Campo,
  Cartao,
  CorpoCartao,
  Esqueleto,
  Pilula,
  TituloCartao,
} from '@/componentes/ui/primitivos';

/**
 * Tarefas agendadas — o cron do WMS.
 *
 * Toda tarefa nasce de uma ROTINA DO CATÁLOGO. Rotina é código do WMS (não
 * se escreve pela tela: teria o acesso do servidor ao ERP e aos canais); a
 * tela escolhe a rotina, preenche os parâmetros que ela declara e define o
 * horário. A mesma rotina pode virar várias tarefas com parâmetros
 * diferentes. "Chamar URL externa" é uma rotina-ponte, para o que ainda mora
 * em outro servidor.
 *
 * A URL costuma carregar senha na query, então a lista mostra os valores
 * mascarados; o endereço inteiro só aparece no formulário de edição.
 */
export function Tarefas(): JSX.Element {
  const consulta = useTarefas();
  const ativar = useAtivarTarefa();
  const executar = useExecutarTarefa();
  const excluir = useExcluirTarefa();
  const catalogo = useCatalogoRotinas();
  /* 'nova' = escolhendo no catálogo; string = criando a partir daquela rotina. */
  const [editando, definirEditando] = React.useState<number | 'nova' | { nova: string } | null>(null);
  const [aberta, definirAberta] = React.useState<number | null>(null);

  const tarefas = consulta.data?.tarefas ?? [];
  const rotinas = catalogo.data?.rotinas ?? [];
  const rotinaDe = (t: TarefaAdmin): RotinaCatalogo | undefined =>
    rotinas.find((r) => r.chave === (t.tipo === 'URL' ? 'url' : t.chave));
  const criando =
    editando !== null && typeof editando === 'object'
      ? rotinas.find((r) => r.chave === editando.nova)
      : undefined;

  return (
    <Cartao>
      <CabecalhoCartao>
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-primaria-suave text-primaria">
            <Clock className="h-4 w-4" />
          </span>
          <div>
            <TituloCartao>Tarefas agendadas</TituloCartao>
            <p className="text-[11.5px] text-textoFraco">
              Rotinas do WMS e URLs chamadas no horário do cron. Todos os horários são de Brasília.
            </p>
          </div>
        </div>
        <Botao
          variante="secundaria"
          tamanho="pequeno"
          onClick={() => definirEditando('nova')}
        >
          <Plus className="h-4 w-4" />
          Nova tarefa
        </Botao>
      </CabecalhoCartao>

      {consulta.isLoading ? (
        <CorpoCartao className="space-y-2">
          {Array.from({ length: 3 }, (_, i) => (
            <Esqueleto key={i} className="h-16" />
          ))}
        </CorpoCartao>
      ) : consulta.isError ? (
        <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      ) : (
        <ul className="divide-y divide-borda">
          {editando === 'nova' ? (
            <li className="bg-primaria-suave/40 p-4">
              <EscolherRotina
                rotinas={rotinas}
                carregando={catalogo.isLoading}
                aoEscolher={(chave) => definirEditando({ nova: chave })}
                aoFechar={() => definirEditando(null)}
              />
            </li>
          ) : null}

          {criando ? (
            <li className="bg-primaria-suave/40 p-4">
              <Formulario rotina={criando} aoFechar={() => definirEditando(null)} />
            </li>
          ) : null}

          {tarefas.length === 0 && editando === null ? (
            <li>
              <EstadoVazio
                titulo="Nenhuma tarefa cadastrada"
                detalhe="Crie uma tarefa a partir de uma rotina do catálogo."
              />
            </li>
          ) : null}

          {tarefas.map((t) =>
            editando === t.id ? (
              <li key={t.id} className="bg-primaria-suave/40 p-4">
                {rotinaDe(t) ? (
                  <Formulario
                    rotina={rotinaDe(t) as RotinaCatalogo}
                    inicial={t}
                    aoFechar={() => definirEditando(null)}
                  />
                ) : (
                  <Esqueleto className="h-24" />
                )}
              </li>
            ) : (
              <li key={t.id}>
                <div className="flex flex-wrap items-center gap-3 px-4 py-3.5">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    onClick={() => definirAberta(aberta === t.id ? null : t.id)}
                    aria-expanded={aberta === t.id}
                  >
                    <ChevronDown
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0 text-textoSuave transition-transform',
                        aberta === t.id ? 'rotate-0' : '-rotate-90',
                      )}
                    />
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[13.5px] font-semibold">{t.nome}</span>
                        {t.tipo === 'INTERNA' ? (
                          <Pilula tom="marca">rotina do WMS</Pilula>
                        ) : (
                          <Pilula tom="alerta">URL externa</Pilula>
                        )}
                        {rotinaDe(t)?.porEmpresa ? (
                          <Pilula tom="neutro">
                            {t.empresas?.length
                              ? `empresa${t.empresas.length > 1 ? 's' : ''} ${t.empresas.join(', ')}`
                              : 'todas as empresas'}
                          </Pilula>
                        ) : null}
                        {t.ativa ? (
                          <Pilula tom="ok">ativa</Pilula>
                        ) : (
                          <Pilula tom="neutro">desativada</Pilula>
                        )}
                      </span>
                      <span className="block truncate font-mono text-[11px] text-textoFraco">
                        {t.tipo === 'URL'
                          ? mascararUrl(t.url ?? '')
                          : resumoParametros(t, rotinaDe(t)) || t.descricao}
                      </span>
                      <span className="tabular block text-[11.5px] text-textoFraco">
                        {descreverCron(t.cron)}
                        <span className="font-mono text-textoSuave"> · {t.cron}</span>
                      </span>
                    </span>
                  </button>

                  <div className="tabular grid min-w-[200px] grid-cols-2 gap-x-4 text-[11.5px]">
                    <div>
                      <span className="block text-[10.5px] uppercase tracking-wide text-textoSuave">
                        Última
                      </span>
                      {t.executando ? (
                        <span className="font-semibold text-primaria">rodando…</span>
                      ) : t.ultimaExecucao ? (
                        <span>
                          <span
                            className={cn(
                              'font-semibold',
                              corStatus(t.ultimoStatus),
                            )}
                          >
                            {t.ultimoStatus}
                          </span>{' '}
                          {quando(t.ultimaExecucao)}
                        </span>
                      ) : (
                        <span className="text-textoSuave">nunca</span>
                      )}
                    </div>
                    <div>
                      <span className="block text-[10.5px] uppercase tracking-wide text-textoSuave">
                        Próxima
                      </span>
                      {t.ativa && t.proximaExecucao ? (
                        <span>{quando(t.proximaExecucao)}</span>
                      ) : (
                        <span className="text-textoSuave">—</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <Botao
                      variante="secundaria"
                      tamanho="pequeno"
                      carregando={executar.isPending && executar.variables === t.id}
                      disabled={t.executando}
                      onClick={() => {
                        definirAberta(t.id);
                        executar.mutate(t.id);
                      }}
                      title="Executa agora, fora do horário"
                    >
                      <Play className="h-3.5 w-3.5" />
                      Executar
                    </Botao>
                    <Botao
                      variante="fantasma"
                      tamanho="iconePequeno"
                      aria-label={t.ativa ? `Desativar ${t.nome}` : `Ativar ${t.nome}`}
                      title={t.ativa ? 'Desativar' : 'Ativar'}
                      carregando={ativar.isPending && ativar.variables?.id === t.id}
                      onClick={() => ativar.mutate({ id: t.id, ativa: !t.ativa })}
                    >
                      {t.ativa ? <Pause className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                    </Botao>
                    <Botao
                      variante="fantasma"
                      tamanho="iconePequeno"
                      aria-label={`Editar ${t.nome}`}
                      title="Editar"
                      onClick={() => definirEditando(t.id)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Botao>
                    {rotinaDe(t) &&
                    !(
                      rotinaDe(t)?.criarPadrao &&
                      tarefas.filter((x) => x.chave === t.chave).length <= 1
                    ) ? (
                      <Botao
                        variante="fantasma"
                        tamanho="iconePequeno"
                        aria-label={`Excluir ${t.nome}`}
                        title="Excluir"
                        carregando={excluir.isPending && excluir.variables === t.id}
                        onClick={() => {
                          if (window.confirm(`Excluir a tarefa "${t.nome}" e o histórico dela?`)) {
                            excluir.mutate(t.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Botao>
                    ) : null}
                  </div>
                </div>

                {aberta === t.id ? <Historico tarefa={t} rotina={rotinaDe(t)} /> : null}
              </li>
            ),
          )}
        </ul>
      )}
    </Cartao>
  );
}

function Historico({
  tarefa,
  rotina,
}: {
  tarefa: TarefaAdmin;
  rotina: RotinaCatalogo | undefined;
}): JSX.Element {
  const consulta = useExecucoesTarefa(tarefa.id);
  const execucoes = consulta.data?.execucoes ?? [];
  return (
    <div className="space-y-3 border-t border-borda bg-superficie2/50 px-4 py-3">
      {rotina ? <ComoFunciona rotina={rotina} recolhido /> : null}
      {tarefa.ultimoResumo ? (
        <p className="mb-2 break-words text-[12px]">
          <span className="font-semibold">Última resposta: </span>
          <span className="font-mono text-textoFraco">{tarefa.ultimoResumo}</span>
        </p>
      ) : null}
      {consulta.isLoading ? (
        <Esqueleto className="h-20" />
      ) : execucoes.length === 0 ? (
        <p className="text-[12px] text-textoSuave">Ainda não executou.</p>
      ) : (
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-[12px]">
            <thead className="sticky top-0 bg-superficie2 text-left text-[10.5px] uppercase tracking-wide text-textoSuave">
              <tr>
                <th className="py-1 pr-3">Quando</th>
                <th className="py-1 pr-3">Status</th>
                <th className="py-1 pr-3">Duração</th>
                <th className="py-1 pr-3">Origem</th>
                <th className="py-1">Resposta</th>
              </tr>
            </thead>
            <tbody className="tabular">
              {execucoes.map((e) => (
                <tr key={e.id} className="border-t border-borda align-top">
                  <td className="whitespace-nowrap py-1 pr-3">{dataHora(e.inicio)}</td>
                  <td
                    className={cn(
                      'py-1 pr-3 font-semibold',
                      corStatus(e.status),
                    )}
                  >
                    {e.status}
                    {e.http ? <span className="font-normal text-textoSuave"> {e.http}</span> : null}
                  </td>
                  <td className="whitespace-nowrap py-1 pr-3">{duracao(e.duracaoMs)}</td>
                  <td className="py-1 pr-3">{e.origem === 'MANUAL' ? 'manual' : 'agenda'}</td>
                  <td className="max-w-[520px] truncate py-1 font-mono text-textoFraco" title={e.resumo ?? ''}>
                    {e.resumo}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/** Passo 1 da tarefa nova: qual rotina. */
function EscolherRotina({
  rotinas,
  carregando,
  aoEscolher,
  aoFechar,
}: {
  rotinas: RotinaCatalogo[];
  carregando: boolean;
  aoEscolher: (chave: string) => void;
  aoFechar: () => void;
}): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[13px] font-semibold">Qual rotina esta tarefa vai executar?</p>
        <Botao variante="fantasma" tamanho="pequeno" onClick={aoFechar}>
          <X className="h-4 w-4" />
          Cancelar
        </Botao>
      </div>
      {carregando ? (
        <Esqueleto className="h-24" />
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {rotinas.map((r) => {
            return (
              <button
                key={r.chave}
                type="button"
                onClick={() => aoEscolher(r.chave)}
                className="rounded-lg border border-borda bg-superficie p-3 text-left transition hover:border-primaria hover:shadow-xs"
              >
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[13px] font-semibold">{r.nome}</span>
                  {r.chave === 'url' ? (
                    <Pilula tom="alerta">ponte</Pilula>
                  ) : (
                    <Pilula tom="marca">rotina do WMS</Pilula>
                  )}
                </span>
                <span className="mt-1 block text-[11.5px] text-textoFraco">{r.descricao}</span>
                <span className="mt-1.5 block text-[10.5px] text-textoSuave">
                  {[
                    r.parametros.length > 0
                      ? `Parâmetros: ${r.parametros.map((p) => p.rotulo).join(', ')}`
                      : 'Sem parâmetros',
                    r.porEmpresa ? 'todas as empresas ou só algumas' : 'não filtra por empresa',
                    r.tarefas > 0 ? `${r.tarefas} tarefa(s) já criada(s)` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            );
          })}
        </div>
      )}
      <p className="text-[11px] text-textoSuave">
        Precisa de uma rotina que não está aqui? Ela é escrita no código do WMS e aparece neste
        catálogo depois de publicada.
      </p>
    </div>
  );
}

const ATALHOS: { rotulo: string; cron: string }[] = [
  { rotulo: '5 min', cron: '*/5 * * * *' },
  { rotulo: '15 min', cron: '*/15 * * * *' },
  { rotulo: '30 min', cron: '*/30 * * * *' },
  { rotulo: 'De hora em hora', cron: '0 * * * *' },
  { rotulo: 'Meia-noite', cron: '0 0 * * *' },
];

/** Passo 2: nome, parâmetros da rotina e horário. Também é a edição. */
function Formulario({
  rotina,
  inicial,
  aoFechar,
}: {
  rotina: RotinaCatalogo;
  inicial?: TarefaAdmin;
  aoFechar: () => void;
}): JSX.Element {
  const salvar = useSalvarTarefa();
  const [nome, definirNome] = React.useState(
    inicial?.nome ?? (rotina.chave === 'url' ? '' : rotina.nome),
  );
  const [descricao, definirDescricao] = React.useState(inicial?.descricao ?? '');
  const [valores, definirValores] = React.useState<Record<string, string>>(() => {
    const atuais: Record<string, string | number> =
      inicial?.tipo === 'URL' ? { url: inicial.url ?? '' } : (inicial?.parametros ?? {});
    return Object.fromEntries(
      rotina.parametros.map((p) => [p.nome, String(atuais[p.nome] ?? p.padrao ?? '')]),
    );
  });
  const { empresas: empresasDisponiveis } = useContexto();
  const [empresas, definirEmpresas] = React.useState<number[]>(inicial?.empresas ?? []);
  const [cron, definirCron] = React.useState(inicial?.cron ?? rotina.cronPadrao);
  const [timeout, definirTimeout] = React.useState(String(inicial?.timeoutSegundos ?? 120));
  const [ativa, definirAtiva] = React.useState(inicial?.ativa ?? true);

  const erroCron = validarCron(cron);
  const proxima = erroCron ? null : proximaExecucaoCron(cron, new Date());
  const faltando = rotina.parametros.find(
    (p) => p.obrigatorio && p.padrao === undefined && !(valores[p.nome] ?? '').trim(),
  );
  const valido = nome.trim() !== '' && !erroCron && !faltando;

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valido) return;
        const parametros: Record<string, string | number> = {};
        for (const p of rotina.parametros) {
          const v = (valores[p.nome] ?? '').trim();
          if (v === '') continue;
          parametros[p.nome] = p.tipo === 'numero' ? Number(v) : v;
        }
        const dados: DadosTarefa = {
          ...(inicial ? {} : { chave: rotina.chave }),
          nome: nome.trim(),
          descricao: descricao.trim() || null,
          parametros,
          empresas: rotina.porEmpresa && empresas.length > 0 ? empresas : null,
          cron: cron.trim(),
          ativa,
          timeoutSegundos: Math.min(600, Math.max(5, Number(timeout) || 120)),
        };
        salvar.mutate({ id: inicial?.id ?? null, dados }, { onSuccess: aoFechar });
      }}
    >
      <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-textoFraco">
        Rotina: <span className="font-semibold text-texto">{rotina.nome}</span>
        {rotina.exigeEscrita ? <Pilula tom="neutro">grava no ERP/canal</Pilula> : null}
      </div>

      <ComoFunciona rotina={rotina} />

      <div className="grid gap-3 sm:grid-cols-2">
        <Rotulado rotulo="Nome da tarefa">
          <Campo
            value={nome}
            onChange={(e) => definirNome(e.currentTarget.value)}
            placeholder="Envio Nota Fiscal Shopee"
          />
        </Rotulado>
        <Rotulado rotulo="Descrição" dica="opcional">
          <Campo value={descricao} onChange={(e) => definirDescricao(e.currentTarget.value)} />
        </Rotulado>
      </div>

      {rotina.parametros.length > 0 ? (
        <fieldset className="space-y-3 rounded-lg border border-borda p-3">
          <legend className="px-1 text-[11px] font-semibold uppercase tracking-wide text-textoFraco">
            Parâmetros da rotina
          </legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {rotina.parametros.map((p) => (
              <div key={p.nome} className={p.tipo === 'url' ? 'sm:col-span-2' : undefined}>
                <Rotulado
                  rotulo={p.rotulo + (p.obrigatorio && p.padrao === undefined ? ' *' : '')}
                  dica={[
                    p.ajuda,
                    p.min !== undefined || p.max !== undefined
                      ? `de ${p.min ?? '…'} a ${p.max ?? '…'}`
                      : null,
                    p.padrao !== undefined ? `padrão ${p.padrao}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                >
                  <Campo
                    className={p.tipo === 'url' ? 'font-mono' : undefined}
                    inputMode={p.tipo === 'numero' ? 'numeric' : undefined}
                    value={valores[p.nome] ?? ''}
                    placeholder={
                      p.tipo === 'url'
                        ? 'https://exemplo.com.br/rotina.asp?senha=...'
                        : String(p.padrao ?? '')
                    }
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      definirValores((a) => ({
                        ...a,
                        [p.nome]: p.tipo === 'numero' ? v.replace(/[^\d.-]/g, '') : v,
                      }));
                    }}
                  />
                </Rotulado>
              </div>
            ))}
          </div>
        </fieldset>
      ) : null}

      {rotina.porEmpresa ? (
        <fieldset className="space-y-2 rounded-lg border border-borda p-3">
          <legend className="px-1 text-[11px] font-semibold uppercase tracking-wide text-textoFraco">
            Empresas
          </legend>
          <div className="flex flex-wrap items-center gap-3 text-[13px]">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={empresas.length === 0} onChange={() => definirEmpresas([])} />
              Todas as empresas
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                checked={empresas.length > 0}
                onChange={() =>
                  definirEmpresas(empresasDisponiveis[0] ? [empresasDisponiveis[0].empresa] : [])
                }
              />
              Só estas:
            </label>
          </div>
          {empresas.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {empresasDisponiveis.map((e) => {
                const marcada = empresas.includes(e.empresa);
                return (
                  <button
                    key={e.empresa}
                    type="button"
                    onClick={() =>
                      definirEmpresas((atual) => {
                        const nova = marcada
                          ? atual.filter((x) => x !== e.empresa)
                          : [...atual, e.empresa].sort((a, b) => a - b);
                        /* Desmarcar a última não vira "todas" sem querer. */
                        return nova.length > 0 ? nova : atual;
                      })
                    }
                    className={cn(
                      'tabular rounded-md border px-2 py-1 text-[12px]',
                      marcada
                        ? 'border-primaria bg-primaria-suave font-semibold text-primaria'
                        : 'border-borda text-textoFraco hover:border-bordaForte',
                    )}
                  >
                    {e.empresa}
                    {e.nome ? ` · ${e.nome}` : ''}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-[11px] text-textoSuave">
              Inclui empresa que for criada depois.
            </p>
          )}
        </fieldset>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <Rotulado
          rotulo="Cron (minuto hora dia mês dia-da-semana), horário de Brasília"
          dica={
            erroCron
              ? erroCron
              : `${descreverCron(cron)} · próxima: ${proxima ? dataHora(proxima.toISOString()) : '—'}`
          }
          erro={Boolean(erroCron)}
        >
          <Campo className="font-mono" value={cron} onChange={(e) => definirCron(e.currentTarget.value)} />
        </Rotulado>
        <Rotulado rotulo="Limite (s)" dica="5 a 600">
          <Campo
            inputMode="numeric"
            className="w-24"
            value={timeout}
            onChange={(e) => definirTimeout(e.currentTarget.value.replace(/\D/g, ''))}
          />
        </Rotulado>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {ATALHOS.map((a) => (
          <Botao
            key={a.cron}
            type="button"
            variante={cron.trim() === a.cron ? 'suave' : 'fantasma'}
            tamanho="micro"
            onClick={() => definirCron(a.cron)}
          >
            {a.rotulo}
          </Botao>
        ))}
      </div>

      <label className="flex items-center gap-2 text-[13px]">
        <input type="checkbox" checked={ativa} onChange={(e) => definirAtiva(e.currentTarget.checked)} />
        Ativa
      </label>

      {salvar.error ? (
        <p className="text-[12px] text-status-divergencia">
          {salvar.error instanceof Error ? salvar.error.message : 'Não foi possível salvar.'}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Botao tamanho="pequeno" type="submit" carregando={salvar.isPending} disabled={!valido}>
          <Check className="h-4 w-4" />
          Salvar
        </Botao>
        <Botao variante="secundaria" tamanho="pequeno" type="button" onClick={aoFechar}>
          <X className="h-4 w-4" />
          Cancelar
        </Botao>
      </div>
    </form>
  );
}

/**
 * O que a rotina faz — só leitura.
 *
 * A lógica é código e não se edita aqui, mas quem liga, desliga ou muda o
 * horário precisa saber o que ela toca. Os passos vêm do catálogo
 * (`internas.ts`), escritos junto com a rotina.
 */
function ComoFunciona({
  rotina,
  recolhido = false,
}: {
  rotina: RotinaCatalogo;
  recolhido?: boolean;
}): JSX.Element {
  return (
    <details
      open={!recolhido}
      className="rounded-lg border border-borda bg-superficie px-3 py-2 text-[12px]"
    >
      <summary className="cursor-pointer select-none text-[11px] font-semibold uppercase tracking-wide text-textoFraco">
        O que esta rotina faz
      </summary>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-texto">
        {rotina.passos.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ol>
      <p className="mt-2 text-[10.5px] text-textoSuave">
        A lógica fica no código do WMS e não se edita pela tela:{' '}
        <span className="font-mono">{rotina.codigo}</span>
      </p>
    </details>
  );
}

function corStatus(status: string | null): string {
  if (status === 'OK') return 'text-status-ok';
  if (status === 'ALERTA') return 'text-status-atencao';
  return 'text-status-divergencia';
}

/** "Esperar após recusa (min): 30" — os parâmetros na linha da lista. */
function resumoParametros(t: TarefaAdmin, r: RotinaCatalogo | undefined): string {
  if (!r || r.parametros.length === 0) return '';
  return r.parametros.map((p) => `${p.rotulo}: ${t.parametros[p.nome] ?? p.padrao ?? '—'}`).join(' · ');
}

function Rotulado({
  rotulo,
  dica,
  erro = false,
  children,
}: {
  rotulo: string;
  dica?: string;
  erro?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block space-y-1">
      <span className="block text-[11px] font-semibold uppercase tracking-wide text-textoFraco">
        {rotulo}
      </span>
      {children}
      {dica ? (
        <span className={cn('block text-[10.5px]', erro ? 'text-status-divergencia' : 'text-textoSuave')}>
          {dica}
        </span>
      ) : null}
    </label>
  );
}

/** Esconde o valor de parâmetros com cara de senha: a lista aparece em tela compartilhada. */
function mascararUrl(texto: string): string {
  try {
    const url = new URL(texto);
    for (const chave of [...url.searchParams.keys()]) {
      if (/senha|pass|token|key|secret|auth|sig/i.test(chave)) url.searchParams.set(chave, '•••');
    }
    return decodeURI(url.toString());
  } catch {
    return texto;
  }
}

function dataHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "08/10 19:30 · há 12 min" / "em 17 min". */
function quando(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  const min = Math.round(Math.abs(diff) / 60_000);
  const rel =
    min < 1
      ? 'agora'
      : min < 60
        ? `${min} min`
        : min < 48 * 60
          ? `${Math.round(min / 60)} h`
          : `${Math.round(min / 1440)} d`;
  const relativo = rel === 'agora' ? 'agora' : diff < 0 ? `há ${rel}` : `em ${rel}`;
  return `${dataHora(iso)} · ${relativo}`;
}

function duracao(ms: number | null): string {
  if (ms === null) return '—';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}
